import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell, refreshNotificationBell } from '../components/Shell.js';
import { showConfirmDialog, showPromptDialog } from '../components/ConfirmDialog.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { formatCrowns } from '../util/formatBig.js';
import type {
  HoldingDto,
  HouseDetailDto,
  HousePerkDto,
  HouseRank,
  HouseSummaryDto,
  MyApplicationDto,
  WarDto,
} from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

type HouseTab = 'members' | 'applications' | 'wars' | 'holdings' | 'perks' | 'chat';

const RANKS: HouseRank[] = ['INITIATE', 'SQUIRE', 'KNIGHT', 'CHANCELLOR', 'LORD'];

function rankAtLeast(actual: HouseRank, needed: HouseRank): boolean {
  return RANKS.indexOf(actual) >= RANKS.indexOf(needed);
}

// Holdings pay daily Renown once every 24h, anchored to each Holding's
// capturedAt (claim and war-forfeit both set capturedAt == lastPaidAt, and the
// lazy server accrual only advances lastPaidAt by whole days — so the schedule
// is always capturedAt + N*24h). That lets the client show an exact countdown
// to the next payout without any extra server data.
const HOLDING_PAYOUT_DAY_MS = 24 * 60 * 60 * 1000;
// Self-clearing interval handle for the "next renown payout" countdown.
let holdingsPayoutTimer: number | null = null;

/** Next payout instant (ms) for a single owned Holding, or null if unknown. */
function nextHoldingPayoutMs(capturedAtIso: string | null, now: number): number | null {
  if (!capturedAtIso) return null;
  const cap = Date.parse(capturedAtIso);
  if (Number.isNaN(cap)) return null;
  const cyclesDone = Math.floor((now - cap) / HOLDING_PAYOUT_DAY_MS);
  return cap + (cyclesDone + 1) * HOLDING_PAYOUT_DAY_MS;
}

/** Compact h/m/s countdown, e.g. "5h 02m 09s". */
function fmtPayoutCountdown(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(sec).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
}

// ── House banner helpers ──────────────────────────────────────────────────────
// Banners are 4:1, matching the game's page-banner art. Uploads are cropped and
// resized client-side to keep the stored data URL small.
export const HOUSE_BANNER_W = 1024;
export const HOUSE_BANNER_H = 256;
const BANNER_DEFAULT = '/img/banners/house-default.webp';
const BANNER_FALLBACK = '/img/banners/houses.webp';
const BANNER_MIMES = 'image/jpeg,image/png,image/webp';
const BANNER_MAX_SOURCE_BYTES = 5 * 1024 * 1024;

/** `src` + onerror fallback for a house banner (its image, else default art, else the page banner). */
function bannerImg(url: string | null | undefined, alt: string, cls: string): string {
  const src = url || BANNER_DEFAULT;
  return `<img class="${cls}" src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" onerror="this.onerror=null;this.src='${BANNER_FALLBACK}'" />`;
}

/** Cover-crop a picked image to 4:1 and return a JPEG data URL. */
async function resizeToBannerDataUrl(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => resolve(null);
      img.onload = () => {
        const targetAspect = HOUSE_BANNER_W / HOUSE_BANNER_H;
        let sw = img.width;
        let sh = Math.round(sw / targetAspect);
        if (sh > img.height) { sh = img.height; sw = Math.round(sh * targetAspect); }
        const sx = (img.width - sw) / 2;
        const sy = (img.height - sh) / 2;
        const canvas = document.createElement('canvas');
        canvas.width = HOUSE_BANNER_W;
        canvas.height = HOUSE_BANNER_H;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, HOUSE_BANNER_W, HOUSE_BANNER_H);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

// ── House-chat unread tracking (client-side, per channel) ─────────────────────
function chatReadKey(houseId: string): string { return `houseChatRead:house:${houseId}`; }
function computeChatUnread(houseId: string, messages: { createdAt: string }[]): number {
  const stored = localStorage.getItem(chatReadKey(houseId));
  if (!stored) { localStorage.setItem(chatReadKey(houseId), new Date().toISOString()); return 0; }
  const cut = Date.parse(stored);
  return messages.reduce((n, m) => (Date.parse(m.createdAt) > cut ? n + 1 : n), 0);
}
function markChatRead(houseId: string): void {
  localStorage.setItem(chatReadKey(houseId), new Date().toISOString());
}

export async function renderHouses(root: HTMLElement): Promise<void> {
  let myHouse: HouseDetailDto | null = null;
  let myApplication: MyApplicationDto | null = null;
  let listings: HouseSummaryDto[] = [];
  let wars: WarDto[] = [];
  let holdings: HoldingDto[] = [];
  let allHouses: HouseSummaryDto[] = [];
  let chatMessages: { createdAt: string }[] = [];
  let flash: FlashState | null = null;
  let activeTab: HouseTab = 'members';

  async function refresh(): Promise<void> {
    try {
      const me = await api.me();
      store.set({ account: me.account, character: me.character });
      const mh = await api.myHouse();
      myHouse = mh.house;
      myApplication = mh.application;
      if (!myHouse) {
        listings = (await api.listHouses()).houses;
      } else {
        // Apply any pending holding payouts on read so renown stays fresh.
        try {
          await api.applyHoldingPayouts();
        } catch {
          // ignore
        }
        wars = (await api.listWars()).wars;
        holdings = (await api.listHoldings()).holdings;
        allHouses = (await api.listHouses()).houses;
        // Recent house-chat messages — only used to compute the unread badge.
        try { chatMessages = (await api.listShouts('house:' + myHouse.id, 50)).messages; } catch { chatMessages = []; }
      }
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Failed to load.';
      flash = { kind: 'danger', message: msg };
    }
  }

  await refresh();

  mountShell(root, (main) => {
    const renderMain = () => {
      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/houses.webp" alt="" />
        <h1>Houses</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          Forge a banner. Or rally beneath one.
        </p>

        <div id="flash"></div>

        <div id="content"></div>
      `;

      const style = document.createElement('style');
      style.textContent = `
        .house-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
          display: flex; flex-direction: column;
        }
        .house-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: var(--s-3);
          margin-top: var(--s-3);
        }
        /* Holdings have a portrait hero image (2:3). Sized so each card hugs
           its artwork — no parchment margins around the image. */
        .holdings-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 240px));
          gap: var(--s-3);
          margin-top: var(--s-3);
          justify-content: start;
        }
        .holding-card {
          background: var(--c-parchment);
          background-image: var(--parchment-texture);
          border: 1px solid var(--c-rule-strong);
          border-radius: var(--radius-lg);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: var(--shadow-soft);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .holding-card:hover {
          transform: translateY(-1px);
          box-shadow: var(--shadow-lift);
        }
        .holding-card.ours { border-color: var(--c-gold); box-shadow: var(--shadow-soft), 0 0 0 2px rgba(212, 169, 60, 0.25); }
        .holding-card.unowned { border-color: var(--c-rule); }
        .holding-banner {
          position: relative;
          width: 100%;
          /* Image fills the card edge-to-edge — no parchment frame around it. */
          aspect-ratio: 2 / 3;
          border-bottom: 1px solid var(--c-rule);
          overflow: hidden;
        }
        .holding-banner img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          image-rendering: -webkit-optimize-contrast;
        }
        .holding-banner-title {
          font-family: var(--font-heading);
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          font-size: 1.25rem;
          color: var(--c-oxblood);
          margin: 0;
        }
        .holding-banner-badge {
          position: absolute;
          top: var(--s-3);
          right: var(--s-3);
          z-index: 1;
          padding: 4px 10px;
          border-radius: 999px;
          font-family: var(--font-display);
          font-size: 0.78rem;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          background: rgba(0, 0, 0, 0.55);
          color: #fff5e1;
          border: 1px solid rgba(255, 245, 220, 0.30);
        }
        .holding-banner-badge.ours {
          background: linear-gradient(to bottom, var(--c-gold-bright), var(--c-gold));
          border-color: #5a3f08;
          color: var(--c-oxblood-deep);
          font-weight: 800;
          text-shadow: 0 1px 0 rgba(255, 245, 220, 0.5);
        }
        .holding-banner-badge.unowned {
          /* Fixed light pill + FIXED dark text — var(--c-ink) flips light in
           * dark mode and vanished on this always-light pill. */
          background: rgba(255, 245, 220, 0.9);
          color: var(--c-oxblood-deep);
          font-weight: 700;
          border-color: rgba(90, 63, 8, 0.5);
        }
        .holding-body {
          padding: var(--s-2) var(--s-3) var(--s-3);
          display: flex;
          flex-direction: column;
          gap: var(--s-2);
          flex: 1 1 auto;
        }
        .holding-body h3 { font-size: 1rem; margin: 0; }
        .holding-blurb {
          font-family: var(--font-elegant);
          font-style: italic;
          color: var(--c-ink-soft);
          margin: 0;
          font-size: 0.85rem;
          line-height: 1.35;
        }
        .holding-pills .pill { font-size: 0.78rem; padding: 1px 8px; }
        .holding-pills {
          display: flex;
          flex-wrap: wrap;
          gap: var(--s-2);
        }
        .holding-owner {
          font-size: 0.92rem;
          color: var(--c-ink-soft);
        }
        .holding-owner strong { color: var(--c-ink); }
        .holding-cta {
          margin-top: auto;
        }
        .members-table {
          width: 100%;
          border-collapse: collapse;
          font-family: var(--font-numeric);
          margin-top: var(--s-3);
        }
        .members-table th, .members-table td {
          text-align: left;
          padding: 6px 10px;
          border-bottom: 1px solid var(--c-rule-soft);
          font-size: 0.92rem;
        }
        .members-table th {
          font-family: var(--font-display);
          color: var(--c-ink-soft);
          background: var(--c-parchment-dark);
        }
        .pill {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule-soft);
          border-radius: 999px;
          padding: 1px 8px;
          font-family: var(--font-numeric);
          font-size: 0.85rem;
        }
        /* (gold pill styling lives in pages.css — don't override here) */
        .rank-badge {
          font-family: var(--font-display);
          font-size: 0.78rem;
          padding: 1px 8px;
          border: 1px solid var(--c-rule-soft);
          border-radius: 3px;
          background: var(--c-parchment-dark);
        }
        .rank-badge.LORD {
          background: linear-gradient(to bottom, var(--c-gold-pale), var(--c-gold-bright) 60%, var(--c-gold));
          color: var(--c-on-gold);
          border-color: var(--c-rule-strong);
          font-weight: 800;
          text-shadow: 0 1px 0 rgba(255, 245, 220, 0.5);
        }
        .rank-badge.CHANCELLOR { color: var(--c-oxblood); }
        .rank-badge.KNIGHT { color: var(--c-success); }
        select.rank-select {
          font: inherit;
          padding: 2px 6px;
          /* Colours come from the global pinned cream cell in styles/pages.css
             (var(--c-parchment) here went dark-on-dark in dark mode). */
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
        }
      `;
      main.appendChild(style);

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      const content = main.querySelector<HTMLDivElement>('#content')!;
      if (myHouse) {
        renderMyHouse(
          content,
          myHouse,
          char.crowns,
          wars,
          holdings,
          allHouses,
          chatMessages,
          activeTab,
          (t) => {
            activeTab = t;
            anchored(renderMain);
          },
          refresh,
          (f) => (flash = f),
          renderMain,
        );
      } else {
        renderListAndCreate(content, listings, myApplication, char.crowns, refresh, (f) => (flash = f), renderMain);
      }
    };

    anchored(renderMain);
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// Sub-renderers
// ──────────────────────────────────────────────────────────────────────────────

function renderMyHouse(
  content: HTMLDivElement,
  house: HouseDetailDto,
  myCrownsStr: string,
  wars: WarDto[],
  holdings: HoldingDto[],
  allHouses: HouseSummaryDto[],
  chatMessages: { createdAt: string }[],
  activeTab: HouseTab,
  setTab: (t: HouseTab) => void,
  refresh: () => Promise<void>,
  setFlash: (f: FlashState) => void,
  rerender: () => void,
): void {
  const myRank = house.myRank!;
  const canManage = rankAtLeast(myRank, 'CHANCELLOR');
  const canTreasury = rankAtLeast(myRank, 'KNIGHT');
  const isLord = myRank === 'LORD';
  const chatUnread = computeChatUnread(house.id, chatMessages);

  const ownedHoldings = holdings.filter((h) => h.ownerHouseId === house.id).length;
  // Total Renown the House earns per day across every Holding it currently owns.
  const holdingsRenownPerDay = holdings
    .filter((h) => h.ownerHouseId === house.id)
    .reduce((sum, h) => sum + h.dailyRenown, 0);
  const activeWars = wars.filter((w) => w.status === 'ACTIVE').length;

  content.innerHTML = `
    <section class="panel">
      ${bannerImg(house.bannerUrl, `${house.name} banner`, 'house-banner')}
      <h2 style="margin-top:var(--s-2);">${escapeHtml(house.name)} <span class="pill">[${escapeHtml(house.tag)}]</span></h2>
      ${house.motto ? `<p style="font-style:italic; color: var(--c-ink-soft);">"${escapeHtml(house.motto)}"</p>` : ''}
      <div style="display:flex; flex-wrap:wrap; gap: var(--s-3); margin-top: var(--s-2);">
        <span class="pill">Members ${house.memberCount}</span>
        <span class="pill gold">Treasury ${formatCrowns(house.treasuryCrowns)} crowns</span>
        <span class="pill">Renown ${house.renown}</span>
        <span class="pill gold">+${holdingsRenownPerDay} renown / day</span>
        <span class="pill">Holdings ${ownedHoldings}</span>
        <span class="pill">Wars ${activeWars}</span>
        <span class="pill">Your rank: <strong>${myRank}</strong></span>
      </div>
      <div style="display:flex; gap: var(--s-2); margin-top: var(--s-3); flex-wrap:wrap;">
        ${isLord ? `<button class="btn ghost" id="change-banner">Change banner</button>` : ''}
        ${canTreasury ? `<button class="btn ghost" id="deposit">Deposit to treasury</button>` : ''}
        <button class="btn ghost" id="leave">Leave house</button>
      </div>
    </section>

    <div style="display:flex; gap: var(--s-2); margin-top: var(--s-4); flex-wrap:wrap;">
      <button class="btn ${activeTab === 'members' ? '' : 'ghost'}" data-house-tab="members">Members</button>
      ${canManage ? `<button class="btn ${activeTab === 'applications' ? '' : 'ghost'}" data-house-tab="applications">Applications${house.pendingApplicationCount > 0 ? ` (${house.pendingApplicationCount})` : ''}</button>` : ''}
      <button class="btn ${activeTab === 'wars' ? '' : 'ghost'}" data-house-tab="wars">Wars (${activeWars})</button>
      <button class="btn ${activeTab === 'holdings' ? '' : 'ghost'}" data-house-tab="holdings">Holdings (${ownedHoldings} / ${holdings.length})</button>
      <button class="btn ${activeTab === 'perks' ? '' : 'ghost'}" data-house-tab="perks">Perk Tree</button>
      <button class="btn ${activeTab === 'chat' ? '' : 'ghost'}" data-house-tab="chat">Chat${chatUnread > 0 ? ` <span class="chat-unread">${chatUnread > 99 ? '99+' : chatUnread}</span>` : ''}</button>
    </div>

    <div id="house-tab-content" style="margin-top: var(--s-3);"></div>
  `;

  // Wire tab switcher
  content.querySelectorAll<HTMLButtonElement>('button[data-house-tab]').forEach((btn) => {
    btn.addEventListener('click', () => {
      setTab(btn.dataset.houseTab as HouseTab);
    });
  });

  const tabContent = content.querySelector<HTMLDivElement>('#house-tab-content')!;
  if (activeTab === 'members') {
    renderMembersTab(tabContent, house, canManage, myRank, refresh, setFlash, rerender);
  } else if (activeTab === 'applications') {
    renderApplicationsTab(tabContent, canManage, refresh, setFlash, rerender);
  } else if (activeTab === 'wars') {
    renderWarsTab(tabContent, house, wars, allHouses, canManage, refresh, setFlash, rerender);
  } else if (activeTab === 'holdings') {
    renderHoldingsTab(tabContent, house, holdings, canManage, refresh, setFlash, rerender);
  } else if (activeTab === 'perks') {
    renderPerksTab(tabContent, house, isLord, refresh, setFlash, rerender);
  } else {
    renderChatTab(tabContent, house, setFlash, chatUnread, rerender);
  }

  // Wire deposit
  content.querySelector<HTMLButtonElement>('#deposit')?.addEventListener('click', async () => {
    const ans = await showPromptDialog({
      title: 'Deposit to the House',
      message: `Deposit how many crowns?\nYou have ${formatCrowns(myCrownsStr)}.`,
      defaultValue: '1000',
      inputType: 'number',
      min: 1,
      confirmLabel: 'Deposit',
      tone: 'gold',
    });
    if (!ans) return;
    if (!/^\d+$/.test(ans.trim())) {
      setFlash({ kind: 'danger', message: 'Amount must be a positive integer.' });
      rerender();
      return;
    }
    try {
      const r = await api.depositTreasury({ amount: ans.trim() });
      store.set({ character: r.character });
      setFlash({ kind: 'success', message: `Deposited ${formatCrowns(ans.trim())} crowns.` });
      await refresh();
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Deposit failed.';
      setFlash({ kind: 'danger', message: msg });
    } finally {
      rerender();
    }
  });

  // Wire banner change (Lord only)
  content.querySelector<HTMLButtonElement>('#change-banner')?.addEventListener('click', () => {
    openBannerDialog(house, async () => { await refresh(); rerender(); });
  });

  // Wire leave
  content.querySelector<HTMLButtonElement>('#leave')?.addEventListener('click', async () => {
    if (!(await showConfirmDialog({
      title: 'Leave the House?',
      message: 'You will lose your rank and any pending House perks. You can join another House later.',
      confirmLabel: 'Leave House',
    }))) return;
    try {
      const r = await api.leaveHouse();
      store.set({ character: r.character });
      setFlash({ kind: 'success', message: 'You have left.' });
      await refresh();
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Leave failed.';
      setFlash({ kind: 'danger', message: msg });
    } finally {
      rerender();
    }
  });

  // Wire rank selectors
  content.querySelectorAll<HTMLSelectElement>('select[data-rank-target]').forEach((sel) => {
    sel.addEventListener('change', async () => {
      const targetId = sel.dataset.rankTarget!;
      const newRank = sel.value as HouseRank;
      try {
        await api.setHouseRank({ targetCharacterId: targetId, newRank });
        setFlash({ kind: 'success', message: `Set rank to ${newRank}.` });
        await refresh();
      } catch (err) {
        const msg = err instanceof HttpError ? err.message : 'Promote failed.';
        setFlash({ kind: 'danger', message: msg });
      } finally {
        rerender();
      }
    });
  });

  // Wire kick
  content.querySelectorAll<HTMLButtonElement>('button[data-kick]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!(await showConfirmDialog({ title: 'Kick member?', message: 'Kick this member from the House?', confirmLabel: 'Kick' }))) return;
      const targetId = btn.dataset.kick!;
      try {
        await api.kickFromHouse({ targetCharacterId: targetId });
        setFlash({ kind: 'success', message: 'Member kicked.' });
        await refresh();
      } catch (err) {
        const msg = err instanceof HttpError ? err.message : 'Kick failed.';
        setFlash({ kind: 'danger', message: msg });
      } finally {
        rerender();
      }
    });
  });
}

function renderListAndCreate(
  content: HTMLDivElement,
  listings: HouseSummaryDto[],
  myApplication: MyApplicationDto | null,
  myCrownsStr: string,
  refresh: () => Promise<void>,
  setFlash: (f: FlashState) => void,
  rerender: () => void,
): void {
  const myCrowns = BigInt(myCrownsStr);
  const hasPending = myApplication !== null;
  content.innerHTML = `
    <section class="panel">
      <h2 style="margin-top:0;">Found a House</h2>
      <p style="font-style:italic; color: var(--c-ink-soft);">Costs 50k crowns. You become Lord.</p>
      <div class="field"><label>Name (3-30 chars)</label><input id="new-name" type="text" maxlength="30" /></div>
      <div class="field"><label>Tag (2-5 chars, A-Z 0-9)</label><input id="new-tag" type="text" maxlength="5" style="text-transform:uppercase" /></div>
      <div class="field"><label>Motto (optional, 280 max)</label><input id="new-motto" type="text" maxlength="280" /></div>
      <button class="btn" id="found-btn" ${myCrowns < 50000n ? 'disabled' : ''}>
        ${myCrowns < 50000n ? 'Not enough crowns' : 'Found House (50k crowns)'}
      </button>
    </section>

    ${
      hasPending
        ? `<section class="panel" style="border-color: var(--c-gold);">
            <p style="margin:0;">⚜ Your petition to <strong>${escapeHtml(myApplication!.houseName)}</strong> <span class="pill">[${escapeHtml(myApplication!.houseTag)}]</span> awaits a Chancellor's word.</p>
            <button class="btn ghost" id="withdraw-app" style="margin-top: var(--s-2);">Withdraw petition</button>
          </section>`
        : ''
    }

    <h2 style="font-family:var(--font-display); color:var(--c-oxblood); border-bottom:1px solid var(--c-rule); padding-bottom:var(--s-1); margin: var(--s-5) 0 var(--s-3) 0;">Existing Houses</h2>
    <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">Membership is by petition — apply, and a Chancellor or the Lord will weigh your worth.</p>
    ${listings.length === 0 ? '<p style="font-style:italic; color: var(--c-ink-soft);">No Houses founded yet — be the first.</p>' : ''}
    <div class="house-list">
      ${listings
        .map(
          (h) => {
            const full = h.memberCount >= 100;
            const btn = hasPending
              ? `<button class="btn" disabled>Petition pending</button>`
              : full
                ? `<button class="btn" disabled>Full</button>`
                : `<button class="btn" data-apply="${h.id}">Apply to Join</button>`;
            return `
        <div class="house-card">
          <h3 style="margin: 0 0 4px 0;"><a href="#/house/${encodeURIComponent(h.id)}">${escapeHtml(h.name)}</a> <span class="pill">[${escapeHtml(h.tag)}]</span></h3>
          ${h.motto ? `<p style="font-style:italic; color: var(--c-ink-soft); font-size: 0.92rem; flex: 1;">"${escapeHtml(h.motto)}"</p>` : '<div style="flex:1"></div>'}
          <div style="display:flex; flex-wrap:wrap; gap: 4px; margin-bottom: var(--s-2); font-size: 0.85rem;">
            <span class="pill">${h.memberCount} / 100 members</span>
            <span class="pill gold">renown ${h.renown}</span>
          </div>
          ${btn}
        </div>
      `;
          },
        )
        .join('')}
    </div>
  `;

  content.querySelector<HTMLButtonElement>('#found-btn')?.addEventListener('click', async () => {
    const nameEl = content.querySelector<HTMLInputElement>('#new-name')!;
    const tagEl = content.querySelector<HTMLInputElement>('#new-tag')!;
    const mottoEl = content.querySelector<HTMLInputElement>('#new-motto')!;
    try {
      const r = await api.createHouse({
        name: nameEl.value,
        tag: tagEl.value,
        motto: mottoEl.value,
      });
      store.set({ character: r.character });
      setFlash({ kind: 'success', message: `${r.house.name} founded — long may it stand.` });
      await refresh();
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Founding failed.';
      setFlash({ kind: 'danger', message: msg });
    } finally {
      rerender();
    }
  });

  content.querySelectorAll<HTMLButtonElement>('button[data-apply]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const id = btn.dataset.apply!;
      btn.disabled = true;
      try {
        const r = await api.applyToHouse({ houseId: id });
        setFlash({ kind: 'success', message: `Your petition to ${r.application.houseName} has been sent.` });
        await refresh();
      } catch (err) {
        const msg = err instanceof HttpError ? err.message : 'Petition failed.';
        setFlash({ kind: 'danger', message: msg });
      } finally {
        rerender();
      }
    });
  });

  content.querySelector<HTMLButtonElement>('#withdraw-app')?.addEventListener('click', async () => {
    try {
      await api.cancelHouseApplication();
      setFlash({ kind: 'success', message: 'Petition withdrawn.' });
      await refresh();
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Could not withdraw.';
      setFlash({ kind: 'danger', message: msg });
    } finally {
      rerender();
    }
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// Tab renderers
// ──────────────────────────────────────────────────────────────────────────────

function renderMembersTab(
  content: HTMLDivElement,
  house: HouseDetailDto,
  canManage: boolean,
  myRank: HouseRank,
  refresh: () => Promise<void>,
  setFlash: (f: FlashState) => void,
  rerender: () => void,
): void {
  content.innerHTML = `
    <div class="table-scroll">
    <table class="members-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Level</th>
          <th>Rank</th>
          <th>Joined</th>
          ${canManage ? '<th>Manage</th>' : ''}
        </tr>
      </thead>
      <tbody>
        ${house.members
          .map(
            (m) => `
          <tr>
            <td><a href="#/u/${encodeURIComponent(m.characterId)}">${escapeHtml(m.name)}</a></td>
            <td>${m.level}</td>
            <td><span class="rank-badge ${m.rank}">${m.rank}</span></td>
            <td>${new Date(m.joinedAt).toLocaleDateString()}</td>
            ${
              canManage && m.rank !== 'LORD'
                ? `<td>
                    <select class="rank-select" data-rank-target="${m.characterId}">
                      ${RANKS.filter((r) => r !== 'LORD' || myRank === 'LORD')
                        .map((r) => `<option value="${r}" ${r === m.rank ? 'selected' : ''}>${r}</option>`)
                        .join('')}
                    </select>
                    <button class="btn ghost" data-kick="${m.characterId}" style="padding:2px 8px; font-size:0.85rem;">Kick</button>
                  </td>`
                : canManage
                  ? '<td>—</td>'
                  : ''
            }
          </tr>
        `,
          )
          .join('')}
      </tbody>
    </table>
    </div>
  `;

  // Wire rank selectors
  content.querySelectorAll<HTMLSelectElement>('select[data-rank-target]').forEach((sel) => {
    sel.addEventListener('change', async () => {
      const targetId = sel.dataset.rankTarget!;
      const newRank = sel.value as HouseRank;
      try {
        await api.setHouseRank({ targetCharacterId: targetId, newRank });
        setFlash({ kind: 'success', message: `Set rank to ${newRank}.` });
        await refresh();
      } catch (err) {
        const msg = err instanceof HttpError ? err.message : 'Promote failed.';
        setFlash({ kind: 'danger', message: msg });
      } finally {
        rerender();
      }
    });
  });

  // Wire kick
  content.querySelectorAll<HTMLButtonElement>('button[data-kick]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!(await showConfirmDialog({ title: 'Kick member?', message: 'Kick this member from the House?', confirmLabel: 'Kick' }))) return;
      const targetId = btn.dataset.kick!;
      try {
        await api.kickFromHouse({ targetCharacterId: targetId });
        setFlash({ kind: 'success', message: 'Member kicked.' });
        await refresh();
      } catch (err) {
        const msg = err instanceof HttpError ? err.message : 'Kick failed.';
        setFlash({ kind: 'danger', message: msg });
      } finally {
        rerender();
      }
    });
  });
}

function renderApplicationsTab(
  content: HTMLDivElement,
  canManage: boolean,
  refresh: () => Promise<void>,
  setFlash: (f: FlashState) => void,
  rerender: () => void,
): void {
  if (!canManage) {
    content.innerHTML = '<p style="font-style:italic; color: var(--c-ink-soft);">Only Chancellors and the Lord may weigh petitions.</p>';
    return;
  }
  content.innerHTML = '<p class="caption">Reading the petitions…</p>';

  void (async () => {
    try {
      const { applications } = await api.listHouseApplications();
      if (applications.length === 0) {
        content.innerHTML = '<p style="font-style:italic; color: var(--c-ink-soft);">No petitions await. The gate is quiet.</p>';
        return;
      }
      content.innerHTML = `
        <div class="table-scroll">
        <table class="members-table">
          <thead><tr><th>Petitioner</th><th>Level</th><th>Their word</th><th>Applied</th><th>Decision</th></tr></thead>
          <tbody>
            ${applications
              .map(
                (a) => `
              <tr>
                <td><a href="#/u/${encodeURIComponent(a.characterId)}">${escapeHtml(a.name)}</a></td>
                <td>${a.level}</td>
                <td>${a.message ? escapeHtml(a.message) : '<span class="caption">—</span>'}</td>
                <td>${new Date(a.appliedAt).toLocaleDateString()}</td>
                <td style="white-space:nowrap;">
                  <button class="btn" data-approve="${a.id}" style="padding:2px 8px; font-size:0.85rem;">Admit</button>
                  <button class="btn ghost" data-reject="${a.id}" style="padding:2px 8px; font-size:0.85rem;">Reject</button>
                </td>
              </tr>`,
              )
              .join('')}
          </tbody>
        </table>
        </div>
      `;

      const review = async (applicationId: string, decision: 'APPROVE' | 'REJECT'): Promise<void> => {
        try {
          await api.reviewHouseApplication({ applicationId, decision });
          setFlash({ kind: 'success', message: decision === 'APPROVE' ? 'Petitioner admitted to the House.' : 'Petition rejected.' });
          await refresh();
        } catch (err) {
          setFlash({ kind: 'danger', message: err instanceof HttpError ? err.message : 'Review failed.' });
        } finally {
          rerender();
        }
      };

      content.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((b) =>
        b.addEventListener('click', () => void review(b.dataset.approve!, 'APPROVE')),
      );
      content.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((b) =>
        b.addEventListener('click', () => void review(b.dataset.reject!, 'REJECT')),
      );
    } catch (err) {
      content.innerHTML = `<p style="color: var(--c-ink-soft);">${escapeHtml(err instanceof HttpError ? err.message : 'Could not load petitions.')}</p>`;
    }
  })();
}

function renderWarsTab(
  content: HTMLDivElement,
  house: HouseDetailDto,
  wars: WarDto[],
  allHouses: HouseSummaryDto[],
  canManage: boolean,
  refresh: () => Promise<void>,
  setFlash: (f: FlashState) => void,
  rerender: () => void,
): void {
  const targets = allHouses.filter((h) => h.id !== house.id);
  const active = wars.filter((w) => w.status === 'ACTIVE');
  const past = wars.filter((w) => w.status !== 'ACTIVE');

  content.innerHTML = `
    ${
      canManage
        ? `<section class="panel">
            <h2 style="margin-top:0;">Declare War</h2>
            <p style="font-style:italic; color: var(--c-ink-soft);">
              Lords and Chancellors only. The first house to reach the respect threshold wins — the loser forfeits one Holding.
            </p>
            ${
              targets.length === 0
                ? '<p style="color: var(--c-ink-soft);">No other houses exist to war against.</p>'
                : `<div style="display:flex; gap: var(--s-2); align-items: flex-end; flex-wrap: wrap;">
                    <div class="field" style="flex: 1; min-width: 220px;">
                      <label>Target House</label>
                      <select id="war-target">
                        ${targets.map((t) => `<option value="${t.id}">${escapeHtml(t.name)} [${escapeHtml(t.tag)}] — ${t.memberCount} members</option>`).join('')}
                      </select>
                    </div>
                    <button class="btn" id="declare-war">Declare War</button>
                  </div>`
            }
          </section>`
        : ''
    }

    <h2 class="award-cat-heading" style="font-family:var(--font-display); color:var(--c-oxblood); border-bottom:1px solid var(--c-rule); padding-bottom:var(--s-1); margin: var(--s-5) 0 var(--s-3) 0;">Active Wars</h2>
    ${
      active.length === 0
        ? '<p style="font-style:italic; color: var(--c-ink-soft);">No active wars.</p>'
        : `<div class="house-list">
            ${active
              .map((w) => {
                const weAreAttacker = w.attackerHouseId === house.id;
                const ourScore = weAreAttacker ? w.attackerScore : w.defenderScore;
                const theirScore = weAreAttacker ? w.defenderScore : w.attackerScore;
                const opponentName = weAreAttacker ? w.defenderName : w.attackerName;
                const opponentTag = weAreAttacker ? w.defenderTag : w.attackerTag;
                const ends = new Date(w.endsAt);
                const remaining = Math.max(0, ends.getTime() - Date.now());
                const hours = Math.floor(remaining / 3600000);
                return `
              <div class="house-card">
                <h3 style="margin: 0 0 4px 0;">${weAreAttacker ? 'vs' : 'attacked by'} ${escapeHtml(opponentName)} <span class="pill">[${escapeHtml(opponentTag)}]</span></h3>
                <div style="display:flex; flex-wrap:wrap; gap: 4px; margin: var(--s-2) 0; font-size: 0.9rem;">
                  <span class="pill">Score: <strong>${ourScore}</strong> vs ${theirScore}</span>
                  <span class="pill gold">Threshold: ${w.respectThreshold}</span>
                  <span class="pill">Ends in ${hours}h</span>
                </div>
                <p style="font-style:italic; color: var(--c-ink-soft); font-size: 0.85rem;">
                  Hit them on the Attacks page — every kill counts toward this war.
                </p>
              </div>
            `;
              })
              .join('')}
          </div>`
    }

    ${
      past.length > 0
        ? `<h2 style="font-family:var(--font-display); color:var(--c-oxblood); border-bottom:1px solid var(--c-rule); padding-bottom:var(--s-1); margin: var(--s-5) 0 var(--s-3) 0;">Past Wars</h2>
           <div class="house-list">
             ${past
               .map((w) => {
                 const weAreAttacker = w.attackerHouseId === house.id;
                 const opponentName = weAreAttacker ? w.defenderName : w.attackerName;
                 const opponentTag = weAreAttacker ? w.defenderTag : w.attackerTag;
                 const weWon =
                   (w.status === 'ATTACKER_WON' && weAreAttacker) ||
                   (w.status === 'DEFENDER_WON' && !weAreAttacker);
                 return `
               <div class="house-card">
                 <h3 style="margin: 0 0 4px 0;">vs ${escapeHtml(opponentName)} <span class="pill">[${escapeHtml(opponentTag)}]</span></h3>
                 <div style="display:flex; flex-wrap:wrap; gap: 4px; font-size: 0.85rem;">
                   <span class="pill ${weWon ? 'gold' : ''}">${w.status}</span>
                   <span class="pill">${w.attackerScore} - ${w.defenderScore}</span>
                   ${w.endedAt ? `<span class="pill">${new Date(w.endedAt).toLocaleDateString()}</span>` : ''}
                 </div>
               </div>
             `;
               })
               .join('')}
           </div>`
        : ''
    }
  `;

  content.querySelector<HTMLButtonElement>('#declare-war')?.addEventListener('click', async () => {
    const sel = content.querySelector<HTMLSelectElement>('#war-target');
    if (!sel) return;
    const target = sel.value;
    if (!(await showConfirmDialog({
      title: 'Declare war?',
      message: 'Active wars cannot be cancelled. The first House to reach the respect threshold wins; the loser forfeits a Holding.',
      confirmLabel: 'Declare War',
    }))) return;
    try {
      await api.declareWar({ defenderHouseId: target });
      setFlash({ kind: 'success', message: 'War declared.' });
      await refresh();
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Declaration failed.';
      setFlash({ kind: 'danger', message: msg });
    } finally {
      rerender();
    }
  });
}

function renderHoldingsTab(
  content: HTMLDivElement,
  house: HouseDetailDto,
  holdings: HoldingDto[],
  canManage: boolean,
  refresh: () => Promise<void>,
  setFlash: (f: FlashState) => void,
  rerender: () => void,
): void {
  const houseRenown = house.renown;
  const ownedByUs = holdings.filter((h) => h.ownerHouseId === house.id);
  // Total Renown earned per day across every Holding this House currently owns.
  const holdingsRenownPerDay = ownedByUs.reduce((sum, h) => sum + h.dailyRenown, 0);
  // Soonest upcoming daily payout across all owned Holdings (null if none owned).
  const nowMs = Date.now();
  const nextPayoutMs = ownedByUs
    .map((h) => nextHoldingPayoutMs(h.capturedAt, nowMs))
    .filter((t): t is number => t !== null)
    .reduce<number | null>((min, t) => (min === null || t < min ? t : min), null);

  content.innerHTML = `
    <section class="panel">
      <h2 style="margin-top:0;">The Realm's Holdings</h2>
      <p style="font-style:italic; color: var(--c-ink-soft);">
        Each Holding pays daily Renown to its owner. Lords and Chancellors may claim unowned Holdings by spending Renown from the treasury.
        War losers forfeit one Holding to the victor.
      </p>
      <div style="display:flex; flex-wrap:wrap; gap: var(--s-3); margin-top: var(--s-2);">
        <span class="pill">Your Renown: <strong>${houseRenown}</strong></span>
        <span class="pill gold">Earning +${holdingsRenownPerDay} renown / day</span>
        ${nextPayoutMs !== null
          ? `<span class="pill" id="holdings-next-payout">Next renown payout in ${fmtPayoutCountdown(nextPayoutMs - nowMs)}</span>`
          : ''}
      </div>
    </section>

    <div class="holdings-grid">
      ${holdings
        .map((h) => {
          const isOurs = h.ownerHouseId === house.id;
          const unowned = h.ownerHouseId === null;
          const canAfford = houseRenown >= h.captureCost;
          const cardClass = isOurs ? 'holding-card ours' : unowned ? 'holding-card unowned' : 'holding-card';
          const badge = isOurs
            ? '<span class="holding-banner-badge ours">Yours</span>'
            : unowned
              ? '<span class="holding-banner-badge unowned">Unclaimed</span>'
              : `<span class="holding-banner-badge">Held</span>`;
          return `
        <div class="${cardClass}">
          <div class="holding-banner">
            <img src="/img/holdings/${h.slug}.webp" alt="${escapeHtml(h.name)}" loading="lazy" />
            ${badge}
          </div>
          <div class="holding-body">
            <p class="holding-blurb">${escapeHtml(h.blurb)}</p>
            <div class="holding-pills">
              <span class="pill gold">+${h.dailyRenown} renown / day</span>
              <span class="pill">Capture: ${h.captureCost} renown</span>
            </div>
            <div class="holding-owner">
              ${
                isOurs
                  ? `Held by your house${h.capturedAt ? ` since <strong>${new Date(h.capturedAt).toLocaleDateString()}</strong>` : ''}.`
                  : unowned
                    ? 'No house currently holds this.'
                    : `Held by <strong>${escapeHtml(h.ownerName ?? '?')}</strong> [${escapeHtml(h.ownerTag ?? '?')}]`
              }
            </div>
            ${
              canManage && unowned
                ? `<div class="holding-cta">
                     <button class="btn" data-claim="${h.slug}" ${canAfford ? '' : 'disabled'}>
                       ${canAfford ? `Claim (${h.captureCost} renown)` : `Need ${h.captureCost} renown`}
                     </button>
                   </div>`
                : ''
            }
          </div>
        </div>
      `;
        })
        .join('')}
    </div>
  `;

  content.querySelectorAll<HTMLButtonElement>('button[data-claim]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const slug = btn.dataset.claim!;
      btn.disabled = true;
      try {
        await api.claimHolding({ holdingSlug: slug });
        setFlash({ kind: 'success', message: 'Holding claimed.' });
        await refresh();
      } catch (err) {
        const msg = err instanceof HttpError ? err.message : 'Claim failed.';
        setFlash({ kind: 'danger', message: msg });
      } finally {
        rerender();
      }
    });
  });

  // Live countdown to the next daily Renown payout (mirrors the bank-interest
  // timer). Tear down any prior ticker first so re-renders/tab-switches don't
  // stack intervals; the ticker also self-clears once its element is gone.
  if (holdingsPayoutTimer !== null) {
    window.clearInterval(holdingsPayoutTimer);
    holdingsPayoutTimer = null;
  }
  if (nextPayoutMs !== null) {
    let settled = false;
    holdingsPayoutTimer = window.setInterval(() => {
      const el = document.getElementById('holdings-next-payout');
      if (!el) {
        if (holdingsPayoutTimer !== null) window.clearInterval(holdingsPayoutTimer);
        holdingsPayoutTimer = null;
        return;
      }
      const remaining = nextPayoutMs - Date.now();
      if (remaining > 0) {
        el.textContent = `Next renown payout in ${fmtPayoutCountdown(remaining)}`;
        return;
      }
      // Due — the server credits on the next read. Settle once and re-render so
      // the renown total and the timer both refresh.
      el.textContent = 'Collecting renown…';
      if (holdingsPayoutTimer !== null) window.clearInterval(holdingsPayoutTimer);
      holdingsPayoutTimer = null;
      if (!settled) {
        settled = true;
        void refresh().then(rerender);
      }
    }, 1000);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Perk Tree tab — shared House-wide perk catalogue (M14).
// Every member sees it; only the Lord can buy/upgrade. Costs deduct from the
// House Treasury + Renown (not the viewer's wallet). Resource-cap perks lift
// every member's bar caps; effect perks (crowns/training/etc.) are read at
// action time by the relevant service.
// ─────────────────────────────────────────────────────────────────────────────

const PERK_CATEGORY_LABELS: Record<HousePerkDto['category'], string> = {
  resources: 'Resources',
  crime: 'Misdeeds',
  honour: 'Honour',
  combat: 'Combat',
  economy: 'Economy',
};

/** "+5 Vigor max", "+10% crowns", "-3% travel time", "+90s chain" — derived
 *  from the perk's effect kind so the UI doesn't need a per-perk string. */
function perkEffectLabel(perk: HousePerkDto, value: number): string {
  switch (perk.effect.kind) {
    case 'bar_max': {
      const bar = perk.effect.bar.charAt(0).toUpperCase() + perk.effect.bar.slice(1);
      return `+${value} ${bar} max`;
    }
    case 'pct_bonus':
      return `+${value}%`;
    case 'pct_discount':
      return `−${value}%`;
    case 'flat_seconds':
      return `+${value}s`;
  }
}

function renderPerksTab(
  content: HTMLDivElement,
  house: HouseDetailDto,
  isLord: boolean,
  refresh: () => Promise<void>,
  setFlash: (f: FlashState) => void,
  rerender: () => void,
): void {
  content.innerHTML = `
    <section class="panel">
      <h3 style="margin-top:0;">House Perk Tree</h3>
      <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -4px;">
        ${isLord
          ? `Only thee, the Lord, may invest the House's Renown and Treasury crowns. Bonuses benefit every member.`
          : `Only the Lord may invest the House's Renown and Treasury crowns. Bonuses benefit every member.`}
      </p>
      <div id="perks-loading" style="color: var(--c-ink-soft);">Loading the perk tree…</div>
      <div id="perks-body"></div>
    </section>
  `;

  const body = content.querySelector<HTMLDivElement>('#perks-body')!;
  const loading = content.querySelector<HTMLDivElement>('#perks-loading')!;

  void api.listHousePerks().then((res) => {
    loading.remove();
    drawPerks(body, res.perks, house, isLord, refresh, setFlash, rerender);
  }).catch((err: unknown) => {
    loading.textContent = err instanceof HttpError
      ? `Could not load perks: ${err.message}`
      : 'Could not load perks.';
  });
}

function drawPerks(
  body: HTMLDivElement,
  perks: HousePerkDto[],
  house: HouseDetailDto,
  isLord: boolean,
  refresh: () => Promise<void>,
  setFlash: (f: FlashState) => void,
  rerender: () => void,
): void {
  // Group by category in the catalogue's natural order.
  const byCat = new Map<HousePerkDto['category'], HousePerkDto[]>();
  for (const p of perks) {
    const arr = byCat.get(p.category) ?? [];
    arr.push(p);
    byCat.set(p.category, arr);
  }

  const treasuryNum = Number(house.treasuryCrowns);

  const sections: string[] = [];
  for (const [cat, list] of byCat) {
    const cards = list.map((p) => {
      const maxed = p.level >= p.maxLevel;
      const next = p.nextCost;
      const canAfford = next ? house.renown >= next.renown && treasuryNum >= next.crowns : false;
      const buyDisabled = !isLord || maxed || !canAfford;
      const buyLabel = maxed ? 'Maxed' : !next ? '—' : `Level up`;
      const costStr = next
        ? `${next.renown} Renown + ${formatCrowns(String(next.crowns))} crowns`
        : '—';
      const currentLabel = p.level > 0 ? perkEffectLabel(p, p.currentBonus) : '—';
      const nextLabel = p.nextBonus !== null ? perkEffectLabel(p, p.nextBonus) : '—';
      return `
        <div class="panel" style="margin: var(--s-2) 0;">
          <div style="display:flex; justify-content:space-between; gap: var(--s-2); flex-wrap:wrap;">
            <div style="flex:1; min-width: 220px;">
              <strong>${escapeHtml(p.name)}</strong>
              <span class="pill" style="margin-left: var(--s-2);">Level ${p.level} / ${p.maxLevel}</span>
            </div>
            <div style="display:flex; gap: var(--s-2); flex-wrap:wrap;">
              <span class="pill">Now: <strong>${currentLabel}</strong></span>
              <span class="pill">Next: <strong>${nextLabel}</strong></span>
            </div>
          </div>
          <p style="font-style:italic; color: var(--c-ink-soft); margin: var(--s-1) 0;">${escapeHtml(p.blurb)}</p>
          <div style="display:flex; justify-content:space-between; gap: var(--s-2); align-items:center; flex-wrap:wrap; margin-top: var(--s-1);">
            <span style="color: var(--c-ink-soft);">Cost: <strong>${costStr}</strong></span>
            <button
              class="btn"
              data-buy-perk="${escapeHtml(p.slug)}"
              ${buyDisabled ? 'disabled' : ''}
              title="${maxed ? 'Maxed' : !isLord ? 'Only the Lord may buy perks.' : !canAfford ? 'Insufficient Renown or crowns.' : ''}"
            >${buyLabel}</button>
          </div>
        </div>
      `;
    }).join('');
    sections.push(`
      <h4 style="margin-top: var(--s-3);">${escapeHtml(PERK_CATEGORY_LABELS[cat])}</h4>
      ${cards}
    `);
  }

  body.innerHTML = sections.join('');

  if (!isLord) return; // Lower ranks: view-only — no button wiring needed.

  body.querySelectorAll<HTMLButtonElement>('button[data-buy-perk]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const slug = btn.dataset.buyPerk!;
      const def = perks.find((p) => p.slug === slug);
      if (!def || !def.nextCost) return;
      const next = def.nextCost;
      const ok = await showConfirmDialog({
        title: `Level up ${def.name}?`,
        message: `Level ${def.level} → ${def.level + 1}. Cost: ${next.renown} Renown + ${formatCrowns(String(next.crowns))} crowns from the House Treasury.`,
        confirmLabel: 'Invest',
        tone: 'gold',
      });
      if (!ok) return;
      try {
        const res = await api.buyHousePerk({ perkSlug: slug });
        setFlash({
          kind: 'success',
          message: `${def.name} is now Level ${res.perk.level}. House Treasury: ${formatCrowns(res.houseTreasury)} crowns, Renown ${res.houseRenown}.`,
        });
        await refresh();
        rerender();
      } catch (err) {
        const msg = err instanceof HttpError ? err.message : 'Could not invest in that perk.';
        setFlash({ kind: 'danger', message: msg });
        rerender();
      }
    });
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Chat tab — house-scoped shoutbox channel.
// ─────────────────────────────────────────────────────────────────────────────

function renderChatTab(
  content: HTMLDivElement,
  house: HouseDetailDto,
  setFlash: (f: FlashState) => void,
  chatUnread: number,
  onRead: () => void,
): void {
  const channel = `house:${house.id}`;

  content.innerHTML = `
    <section class="panel">
      <h3 style="margin-top:0;">House Chat</h3>
      <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -4px;">
        Speak only with thy housemates. Words here are seen by no other house.
      </p>
      <div id="house-chat-feed" style="
        max-height: 360px; overflow-y: auto;
        background: var(--c-parchment);
        border: 1px solid var(--c-rule);
        border-radius: var(--radius);
        padding: var(--s-2);
        margin-top: var(--s-2);
        font-family: var(--font-body);
        line-height: 1.45;
      ">
        <div class="caption">Loading…</div>
      </div>
      <div style="display:flex; gap: var(--s-2); margin-top: var(--s-2);">
        <input id="house-chat-input" type="text" maxlength="500" placeholder="Speak to thy house…"
          style="flex:1; padding: 8px 12px; font: inherit; background: rgba(255,250,235,0.65);
                 border: 1px solid var(--c-rule); border-radius: var(--radius);" />
        <button class="btn" id="house-chat-send">Post</button>
      </div>
      <p class="caption" style="margin-top: var(--s-2);">500 chars max · house members only.</p>
    </section>
  `;

  const feed = content.querySelector<HTMLDivElement>('#house-chat-feed')!;
  const input = content.querySelector<HTMLInputElement>('#house-chat-input')!;
  const sendBtn = content.querySelector<HTMLButtonElement>('#house-chat-send')!;

  const loadMessages = async (): Promise<void> => {
    try {
      const r = await api.listShouts(channel, 50);
      if (r.messages.length === 0) {
        feed.innerHTML = '<div class="caption">No words yet. Start the conversation.</div>';
        return;
      }
      feed.innerHTML = r.messages.map((m) => `
        <div style="margin-bottom: 6px;">
          <strong style="color: var(--c-oxblood);">${escapeHouseChatHtml(m.characterName)}</strong>
          <span class="caption" style="margin-left: 6px;">${new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          <div>${escapeHouseChatHtml(m.body)}</div>
        </div>
      `).join('');
      feed.scrollTop = feed.scrollHeight;
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Could not load chat.';
      feed.innerHTML = `<div class="caption">${escapeHouseChatHtml(msg)}</div>`;
    }
  };

  void loadMessages();

  // Opening the chat also clears its bell notifications server-side, so the
  // notification bell agrees with the chat being seen (best-effort; nudges the
  // badge immediately rather than waiting for its 30s poll). Unconditional —
  // the bell can hold entries even when the per-device unread badge reads 0.
  void api.markHouseChatNotificationsRead().then(() => refreshNotificationBell()).catch(() => {});

  // Opening the chat marks it read — clears the unread badge on next render.
  if (chatUnread > 0) {
    markChatRead(house.id);
    onRead();
  }

  const send = async (): Promise<void> => {
    const body = input.value.trim();
    if (body.length === 0) return;
    sendBtn.disabled = true;
    try {
      await api.postShout(channel, body);
      input.value = '';
      await loadMessages();
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Could not post.';
      setFlash({ kind: 'danger', message: msg });
    } finally {
      sendBtn.disabled = false;
    }
  };

  sendBtn.addEventListener('click', () => { void send(); });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  });
}

function escapeHouseChatHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// ─── House banner dialog ──────────────────────────────────────────────────────
// Reuses the avatar dialog's overlay styling. Single image (no library): upload
// replaces, Remove reverts to the default art. Lord-only (callers gate on rank).
function openBannerDialog(house: HouseDetailDto, onDone: () => void): void {
  const overlay = document.createElement('div');
  overlay.className = 'avatar-dialog-overlay';
  overlay.innerHTML = `
    <div class="avatar-dialog" role="dialog" aria-labelledby="banner-dialog-title">
      <h2 id="banner-dialog-title">House Banner</h2>
      <p class="avatar-dialog-instructions">
        A wide <strong>4:1</strong> banner for your House — shown here, on your public House page,
        and small in the Annals. JPG, PNG, or WebP, max <strong>5 MB</strong>; cropped and resized to
        ${HOUSE_BANNER_W}&times;${HOUSE_BANNER_H} before upload.
      </p>
      <div class="banner-dialog-preview">${bannerImg(house.bannerUrl, 'Current banner', 'house-banner')}</div>
      <div class="avatar-dialog-actions">
        <input type="file" id="banner-upload-input" accept="${BANNER_MIMES}" hidden />
        <button class="btn" id="banner-upload-btn">Upload banner</button>
        ${house.bannerUrl ? `<button class="btn-secondary" id="banner-remove-btn">Remove (use default)</button>` : ''}
        <button class="btn-secondary" id="banner-close-btn">Close</button>
      </div>
      <div class="avatar-dialog-status" id="banner-status" role="status"></div>
    </div>`;
  document.body.appendChild(overlay);

  const statusEl = overlay.querySelector<HTMLDivElement>('#banner-status')!;
  const uploadBtn = overlay.querySelector<HTMLButtonElement>('#banner-upload-btn')!;
  const fileInput = overlay.querySelector<HTMLInputElement>('#banner-upload-input')!;
  const closeBtn = overlay.querySelector<HTMLButtonElement>('#banner-close-btn')!;
  const removeBtn = overlay.querySelector<HTMLButtonElement>('#banner-remove-btn');
  const setStatus = (msg: string, kind: 'info' | 'error' | 'success' = 'info'): void => {
    statusEl.textContent = msg;
    statusEl.className = `avatar-dialog-status status-${kind}`;
  };
  const close = (): void => overlay.remove();

  closeBtn.addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  uploadBtn.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    fileInput.value = '';
    if (!file) return;
    if (file.size > BANNER_MAX_SOURCE_BYTES) {
      setStatus(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.`, 'error');
      return;
    }
    setStatus('Resizing image…');
    const dataUrl = await resizeToBannerDataUrl(file);
    if (!dataUrl) { setStatus('Could not read that image — try another.', 'error'); return; }
    setStatus('Uploading…');
    try {
      await api.setHouseBanner({ bannerDataUrl: dataUrl });
      setStatus('Banner updated.', 'success');
      close();
      onDone();
    } catch (e) {
      setStatus(e instanceof HttpError ? e.message : 'Upload failed.', 'error');
    }
  });

  removeBtn?.addEventListener('click', async () => {
    removeBtn.disabled = true;
    try {
      await api.clearHouseBanner();
      setStatus('Banner removed.', 'success');
      close();
      onDone();
    } catch (e) {
      removeBtn.disabled = false;
      setStatus(e instanceof HttpError ? e.message : 'Remove failed.', 'error');
    }
  });
}
